import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Images } from "lucide-react";

export const PhotoGallery = ({ images, title }) => {
  const imgs = images?.length ? images : ["https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=85"];
  const [active, setActive] = useState(0);
  const trackRef = useRef(null);

  const scrollToIndex = (i) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(imgs.length - 1, i));
    track.scrollTo({ left: clamped * track.clientWidth, behavior: "smooth" });
    setActive(clamped);
  };

  const onScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const i = Math.round(track.scrollLeft / track.clientWidth);
    if (i !== active) setActive(i);
  };

  return (
    <>
      {/* Mobile: swipeable carousel showing every photo */}
      <div className="md:hidden relative" data-testid="mobile-gallery">
        <div
          ref={trackRef}
          onScroll={onScroll}
          className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar rounded-xl h-[300px]"
        >
          {imgs.map((src, i) => (
            <div key={i} className="min-w-full snap-center bg-line">
              <img src={src} alt={i === 0 ? title : `${title} photo ${i + 1}`} className="h-full w-full object-cover" draggable={false} />
            </div>
          ))}
        </div>

        {imgs.length > 1 && (
          <>
            <span data-testid="gallery-counter" className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full bg-ink/75 text-white text-xs font-medium px-2.5 py-1">
              <Images className="h-3 w-3" /> {active + 1}/{imgs.length}
            </span>
            <button aria-label="Previous photo" data-testid="gallery-prev" onClick={() => scrollToIndex(active - 1)} disabled={active === 0}
              className="absolute left-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/85 text-ink flex items-center justify-center shadow-sm disabled:opacity-0 transition-opacity">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button aria-label="Next photo" data-testid="gallery-next" onClick={() => scrollToIndex(active + 1)} disabled={active === imgs.length - 1}
              className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/85 text-ink flex items-center justify-center shadow-sm disabled:opacity-0 transition-opacity">
              <ChevronRight className="h-5 w-5" />
            </button>
            <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
              {imgs.map((_, i) => (
                <button key={i} aria-label={`Go to photo ${i + 1}`} onClick={() => scrollToIndex(i)}
                  className={`h-1.5 rounded-full transition-all ${i === active ? "w-5 bg-white" : "w-1.5 bg-white/60"}`} />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Desktop: bento gallery */}
      <div className="hidden md:grid md:grid-cols-3 gap-3 h-[440px]">
        <div className="md:col-span-2 rounded-xl overflow-hidden bg-line">
          <img src={imgs[0]} alt={title} className="h-full w-full object-cover" />
        </div>
        <div className="grid grid-rows-2 gap-3">
          <div className="rounded-xl overflow-hidden bg-line">
            <img src={imgs[1] || imgs[0]} alt="" className="h-full w-full object-cover" />
          </div>
          <div className="rounded-xl overflow-hidden bg-line relative">
            <img src={imgs[2] || imgs[0]} alt="" className="h-full w-full object-cover" />
            {imgs.length > 3 && (
              <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-ink/75 text-white text-xs font-medium px-2.5 py-1">
                <Images className="h-3 w-3" /> +{imgs.length - 3} more
              </span>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
